import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const BASE_URL = config.BASE_URL;

function severityBadge(severity) {
  const map = {
    CRITICAL: { bg: '#dc2626', color: '#fff' },
    HIGH:     { bg: '#ea580c', color: '#fff' },
    MEDIUM:   { bg: '#ca8a04', color: '#fff' },
    LOW:      { bg: '#2563eb', color: '#fff' },
  };
  const s = map[(severity || '').toUpperCase()] || { bg: '#6b7280', color: '#fff' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: '4px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
      {severity || 'UNKNOWN'}
    </span>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="card text-center" style={{ minWidth: 110, flex: '1 1 110px' }}>
      <div className="card-body py-2 px-3">
        <div style={{ fontSize: '1.6rem', fontWeight: 700, color }}>{value}</div>
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{label}</div>
      </div>
    </div>
  );
}

function MatchRow({ match, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <td><code style={{ fontSize: '0.8rem' }}>{match.cve || '—'}</code></td>
        <td><code style={{ fontSize: '0.8rem' }}>{match.packageName}</code></td>
        <td style={{ color: '#6b7280', fontSize: '0.82rem' }}>{match.packageVersion || '—'}</td>
        <td>{severityBadge(match.severity)}</td>
        <td style={{ fontSize: '0.82rem' }}>{match.cvssScore ? match.cvssScore.toFixed(1) : '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{match.publisher || '—'}</td>
        <td style={{ fontSize: '0.82rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{match.advisoryTitle || match.advisoryId}</td>
        <td style={{ fontSize: '0.82rem', color: match.remediation ? '#16a34a' : '#9ca3af' }}>
          {match.remediation ? '✓' : '—'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: '#f9fafb', borderTop: 0 }}>
            <div style={{ padding: '0.75rem 1rem', fontSize: '0.82rem' }}>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('csaf.summaryLabel')}</div>
                  <div style={{ color: '#374151' }}>{match.summary || '—'}</div>
                  {match.cweId && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <span style={{ fontWeight: 600 }}>{t('csaf.cweLabel')}: </span>
                      <code>{match.cweId}</code>{match.cweName ? ` — ${match.cweName}` : ''}
                    </div>
                  )}
                </div>
                <div style={{ flex: '1 1 260px' }}>
                  {match.remediation && (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('csaf.remediationLabel')}</div>
                      <div style={{ color: '#374151', marginBottom: '0.35rem' }}>{match.remediation}</div>
                    </>
                  )}
                  {match.remediationUrl && (
                    <div>
                      <span style={{ fontWeight: 600 }}>{t('csaf.vendorFixUrl')}: </span>
                      <a href={match.remediationUrl} target="_blank" rel="noopener noreferrer">{match.remediationUrl}</a>
                    </div>
                  )}
                  {match.fixedProducts && match.fixedProducts.length > 0 && (
                    <div style={{ marginTop: '0.35rem' }}>
                      <span style={{ fontWeight: 600 }}>{t('csaf.fixedIn')}: </span>
                      {match.fixedProducts.join(', ')}
                    </div>
                  )}
                  <div style={{ marginTop: '0.35rem', color: '#6b7280' }}>
                    <span style={{ fontWeight: 600 }}>Advisory ID: </span>{match.advisoryId}
                    {match.date && <span> · {match.date.slice(0, 10)}</span>}
                  </div>
                  {match.vectorString && (
                    <div style={{ marginTop: '0.25rem', color: '#6b7280', fontSize: '0.75rem' }}>
                      <code>{match.vectorString}</code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CsafView({ repoName, sbomId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Import panel
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importError, setImportError] = useState('');

  // Advisory list modal
  const [advisoryModalOpen, setAdvisoryModalOpen] = useState(false);
  const [advisories, setAdvisories] = useState([]);
  const [advisoriesLoading, setAdvisoriesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const sbomFileName = sbomId ? `${sbomId}-sbom.json` : null;

  const loadData = useCallback(() => {
    if (!repoName || !sbomFileName) return;
    setLoading(true);
    setError('');
    authFetch(`${BASE_URL}/sbom/${repoName}/${sbomFileName}/csaf`)
      .then(res => res.json())
      .then(d => setData(d))
      .catch(err => setError(err.message || 'Failed to load CSAF data'))
      .finally(() => setLoading(false));
  }, [repoName, sbomFileName]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAdvisories = useCallback(() => {
    setAdvisoriesLoading(true);
    authFetch(`${BASE_URL}/csaf/advisories`)
      .then(res => res.json())
      .then(d => setAdvisories(d || []))
      .catch(() => setAdvisories([]))
      .finally(() => setAdvisoriesLoading(false));
  }, []);

  const handleOpenAdvisoryModal = () => {
    setAdvisoryModalOpen(true);
    loadAdvisories();
  };

  const handleImport = async () => {
    setImporting(true);
    setImportMsg('');
    setImportError('');
    try {
      let body;
      try {
        body = JSON.parse(importJson);
      } catch {
        setImportError('Invalid JSON');
        setImporting(false);
        return;
      }
      const res = await authFetch(`${BASE_URL}/csaf/advisories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Import failed');
      }
      setImportMsg(t('csaf.importSuccess'));
      setImportJson('');
      loadData();
    } catch (err) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('csaf.deleteConfirm'))) return;
    setDeletingId(id);
    try {
      const res = await authFetch(`${BASE_URL}/csaf/advisories/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Delete failed');
      }
      setAdvisories(a => a.filter(x => x.advisoryId !== id));
      loadData();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <div>
          <h5 style={{ margin: 0, fontWeight: 700 }}>📋 {t('csaf.title')}</h5>
          <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: '0.25rem' }}>{t('csaf.subtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={handleOpenAdvisoryModal}>
            {t('csaf.advisoriesBtn')}
          </button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setImportOpen(o => !o)}>
            {importOpen ? '▲' : '▼'} {t('csaf.importTitle')}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={loadData} disabled={loading}>
            ↺ {t('csaf.scanBtn')}
          </button>
        </div>
      </div>

      {/* Import panel */}
      {importOpen && (
        <div className="card mb-3" style={{ border: '1px solid #d1d5db' }}>
          <div className="card-body">
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{t('csaf.importTitle')}</div>
            <div style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{t('csaf.importHint')}</div>
            <textarea
              className="form-control"
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
              placeholder={t('csaf.importPlaceholder')}
              value={importJson}
              onChange={e => setImportJson(e.target.value)}
            />
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-sm btn-primary" onClick={handleImport} disabled={importing || !importJson.trim()}>
                {importing ? '…' : t('csaf.importBtn')}
              </button>
              {importMsg && <span style={{ color: '#16a34a', fontSize: '0.82rem' }}>✓ {importMsg}</span>}
              {importError && <span style={{ color: '#dc2626', fontSize: '0.82rem' }}>✗ {importError}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {data && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <SummaryCard label={t('csaf.totalMatches')} value={data.matchedCVEs} color="#111827" />
          <SummaryCard label="Critical" value={data.critical} color="#dc2626" />
          <SummaryCard label="High" value={data.high} color="#ea580c" />
          <SummaryCard label="Medium" value={data.medium} color="#ca8a04" />
          <SummaryCard label="Low" value={data.low} color="#2563eb" />
          <SummaryCard label="Advisories" value={data.totalAdvisories} color="#6b7280" />
        </div>
      )}

      {/* Body */}
      {loading && <Skeleton lines={6} />}
      {!loading && error && (
        <div className="alert alert-danger" style={{ fontSize: '0.85rem' }}>{error}</div>
      )}
      {!loading && !error && data && data.matches && data.matches.length === 0 && (
        <EmptyState
          icon="📋"
          title={t('csaf.noMatches')}
          description={t('csaf.noMatchesHint')}
        />
      )}
      {!loading && !error && data && data.matches && data.matches.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover" style={{ fontSize: '0.83rem' }}>
            <thead>
              <tr>
                <th>{t('csaf.columns.cve')}</th>
                <th>{t('csaf.columns.package')}</th>
                <th>{t('csaf.columns.version')}</th>
                <th>{t('csaf.columns.severity')}</th>
                <th>{t('csaf.columns.cvss')}</th>
                <th>{t('csaf.columns.publisher')}</th>
                <th>{t('csaf.columns.advisory')}</th>
                <th>{t('csaf.columns.remediation')}</th>
              </tr>
            </thead>
            <tbody>
              {data.matches.map((m, i) => (
                <MatchRow key={`${m.cve}-${m.packageName}-${i}`} match={m} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Advisory list modal */}
      {advisoryModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ width: '90%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>{t('csaf.advisoriesTitle')}</strong>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setAdvisoryModalOpen(false)}>✕</button>
            </div>
            <div className="card-body" style={{ overflowY: 'auto' }}>
              {advisoriesLoading && <div className="text-muted text-center py-3">Loading…</div>}
              {!advisoriesLoading && advisories.length === 0 && (
                <div className="text-muted text-center py-3">{t('csaf.noAdvisories')}</div>
              )}
              {!advisoriesLoading && advisories.length > 0 && (
                <table className="table table-sm" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Publisher</th>
                      <th>Severity</th>
                      <th>CVEs</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {advisories.map(a => (
                      <tr key={a.advisoryId}>
                        <td><code style={{ fontSize: '0.75rem' }}>{a.advisoryId}</code><div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{a.title}</div></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{a.publisher}</td>
                        <td>{severityBadge(a.severity)}</td>
                        <td>{a.cveCount}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{a.date ? a.date.slice(0, 10) : '—'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{ padding: '1px 8px', fontSize: '0.72rem' }}
                            disabled={deletingId === a.advisoryId}
                            onClick={() => handleDelete(a.advisoryId)}
                          >
                            {t('csaf.deleteAdvisory')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
